import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/shared/ui';
import type { Role } from '../../model/types';

interface RoleChangePopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  roles: Role[];
  currentRoleId: string;
  onApply: (roleId: string) => Promise<void>;
}

export function RoleChangePopover({
  anchorEl,
  open,
  onClose,
  roles,
  currentRoleId,
  onApply,
}: RoleChangePopoverProps) {
  const [search, setSearch] = useState('');
  const [pendingRoleId, setPendingRoleId] = useState(currentRoleId);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch('');
      setPendingRoleId(currentRoleId);
    }
  }, [open, currentRoleId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return roles;
    return roles.filter((r) =>
      r.name?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [roles, search]);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      await onApply(pendingRoleId);
      onClose();
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 280, borderRadius: 2 } } }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Change Role
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search roles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
        />
      </Box>
      <Divider />
      <List
        dense
        disablePadding
        role="listbox"
        sx={{ maxHeight: 240, overflow: 'auto' }}
      >
        {filtered.map((role) => {
          return (
            <ListItemButton
              key={role.id}
              role="option"
              aria-selected={pendingRoleId === role.id}
              selected={pendingRoleId === role.id}
              onClick={() => setPendingRoleId(role.id)}
              sx={{
                px: 2,
                '&.Mui-selected': { bgcolor: '#EDF0FF' },
                '&.Mui-selected:hover': { bgcolor: '#E5E9FF' },
              }}
            >
              <ListItemText
                primary={role.name ?? '—'}
                secondary={role.description}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </ListItemButton>
          );
        })}
        {filtered.length === 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            No roles match
          </Box>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          color="secondary"
          variant="outlined"
          sx={{ px: 2, height: 36, borderRadius: 1.5 }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          sx={{ px: 2, height: 36, borderRadius: 1.5 }}
          disabled={pendingRoleId === currentRoleId || isApplying}
          onClick={handleApply}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
}
